import React, { useCallback } from 'react';
import { useNavigationContext } from 'Organisms/NavigationWithContext/NavigationContext';
import { useModalContext } from 'Views/Page/Contexts/ModalContext';
import Portal from 'Components/ComponentLibrary/ExternalLibraries/Libraries/MaterialV4/Portal';
import fr from 'facet-rest';
import { FDEmphasisSurface, FDStack, FDTextButton } from '@facet-wealth/design';
import { usePageContentContext } from 'Views/Page/Contexts/PageContentContext';
import { buildUseStyles } from 'Components/ComponentLibrary/ExternalLibraries/Libraries/MaterialV4/styles';
import FullScreenDataContainer
  from 'Components/ComponentLibrary/Components/Uncategorized/DataContainer/FullScreenDataContainer';
import NullComponent from 'Components/UI/NullComponent';
import { extractParamFromLink } from 'Components/UI/roadmapDeliverable/leftNav/extractParamFromLink';
import { dataAttrs } from 'Helpers/Testing';
import { useRightColumnContext } from 'Context/RightColumnContext';
import { useHistory } from 'react-router-dom';
import { NavItems } from './navItems';

const useStyles = buildUseStyles('RoadmapDeliverableLeftNav', (theme) => ({
  background: {
    backgroundColor: theme.palette.facet.primaryBlue,
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    height: '100%',
    width: '100%',
  },
  loader: {
    position: 'fixed',
    top: 0,
    left: 0,
    width: '100vw',
    '& .MuiCircularProgress-svg': {
      color: theme.palette.common.white,
    },
  },
}));

export const RoadmapDeliverableLeftNav = () => {
  const classes = useStyles();
  const { navPortal, navigationResource, setOpen } = useNavigationContext();
  const { isInitializing } = useModalContext();
  const { isPageContentLoading, loadPageContentLink } = usePageContentContext();
  const closeNav = useCallback(() => setOpen(false), [setOpen]);
  const { loadRightColumnCollectionLink, rightColumnCollectionLoading } = useRightColumnContext();
  const history = useHistory();

  const computeOnClickFor = (item) => async () => {
    const relatedLink = item.relationship(fr.relationships.navigate.name)?.link('related');
    if (!relatedLink) {
      return;
    }

    // Get the original URL string
    const originalLink = relatedLink.url();

    // Extract the index and get the cleaned URL
    const { cleanedLink, paramValue: index } = extractParamFromLink(originalLink, 'index');
    if (index) {
      // set index on the current page url
      const searchParams = new URLSearchParams(history.location.search);
      searchParams.set('index', index);

      history.push({
        pathname: history.location.pathname,
        search: searchParams.toString(),
      });
    }

    await loadPageContentLink(cleanedLink);
    closeNav();
  };

  const computeTitleFor = (item) => item.attr('title');

  if (!navPortal) {
    return null;
  }

  const ScenarioQuestionForPlannerButton = navigationResource.withRelationship(fr.relationships.scenarioQuestionForPlanner.name, (rel) => {
    return (
      <FDTextButton
        appearance={'standalone'}
        label={rel.meta('prompt')}
        icon={'message'}
        size={'sm'}
        loading={rightColumnCollectionLoading ?? false}
        onClick={() => {
          loadRightColumnCollectionLink(rel.link('related'));
        }}
        {...dataAttrs('ask-my-planner-button')}
      />
    );
  },
  () => null);

  return (
    <Portal container={() => navPortal}>
      <div className={classes.loader}>
        <FullScreenDataContainer
          component={NullComponent}
          loading={isInitializing || isPageContentLoading}
        />
      </div>
      <FDEmphasisSurface flex={1} height="100%" rounded={false} width={'100%'}>
        <div className={classes.background}>
          <FDStack direction="column" spacing={3} flex={1} justifyContent="space-between" width={'100%'}>
            <FDStack direction="column" spacing={2} flex={1}>
              <NavItems
                items={navigationResource?.attr('children')?.resources()?.list() ?? []}
                computeOnClickFor={computeOnClickFor}
                computeTitleFor={computeTitleFor}
              />
            </FDStack>
            <div style={{ borderTop: '1px solid var(--color-oceana-300, #549FBB)', width: '100%' }} />
            <FDStack direction="column" spacing={1} alignItems={'flex-start'}>
              <FDTextButton
                label={'Dashboard'}
                icon={'navDashboard'}
                size={'sm'}
                loading={rightColumnCollectionLoading ?? false}
                onClick={() => {
                  history.push('/');
                }}
                {...dataAttrs('ask-my-planner-button')}
              />
              {ScenarioQuestionForPlannerButton}
            </FDStack>
          </FDStack>
        </div>
      </FDEmphasisSurface>
    </Portal>
  );
};
